import type { WorkbookDashboard, WorkbookSheet } from "../../../shared/account-finance/finance-workbook-contract";
import { convertWorkbookAmount } from "./finance-workbook-conversion";
import type { DisplayCurrency } from "./finance-workbook-conversion";
import { workbookLaneTotal, workbookRowIncluded, workbookUniqueRows } from "./finance-workbook-presentation";

export type FinanceGranularity = "day" | "week" | "month";
export type FinanceCashBasis = "recorded" | "available";
export type FinancePeriod = { key: string; label: string; from: string; to: string; partial: boolean };
export type FinanceSourceIssue = {
  sheetId: string;
  kind: "missing" | "partial" | "error" | "wrong-date" | "wrong-company" | "undated";
  message: string;
};
export type FinanceCashSource = {
  id: string; sheetId: string; label: string; kind: "opening" | "incoming" | "outgoing";
  category?: string; included: boolean; available: boolean; issue: FinanceSourceIssue | null;
};
export type FinanceCashRecord = {
  key: string; sheetId: string; movementId: string; row: WorkbookSheet["rows"][number];
  /** Signed contribution in the record's native currency, in minor units. */
  amount: bigint; included: boolean;
};
/** currency filters native records; the selected display currency is a separate argument. */
export type FinanceDetailScope = {
  rowId: string; from: string; to: string; bucket?: "period" | "overdue" | "undated";
  currency?: string; includePlans?: boolean; granularity?: FinanceGranularity;
  basis?: FinanceCashBasis;
  category?: string; direction?: "incoming" | "outgoing";
};
export type FinanceDetailResult = {
  records: FinanceCashRecord[]; totals: { currency: string; amount: bigint | null }[];
  issues: FinanceSourceIssue[]; complete: boolean;
};

export const financePlanLabels: Readonly<Record<string, string>> = {
  maas: "Maaş ve personel", sgk: "SGK", vergi: "Vergi", kira: "Kira", enerji: "Enerji", diger: "Diğer planlar",
};
const iso = (date: Date) => date.toISOString().slice(0, 10);
const parseDate = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) throw new Error("Rapor tarihi geçersiz.");
  const date = new Date(`${value}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || iso(date) !== value) throw new Error("Rapor tarihi geçersiz.");
  return date;
};
const shortDay = (date: Date) => new Intl.DateTimeFormat("tr-TR", { day: "numeric", month: "short", timeZone: "UTC" }).format(date);

/** Thirteen consecutive seven-day periods: report day and the following 90 days, both ends included. */
export function financeTimelinePeriods(date: string, granularity: FinanceGranularity = "month"): FinancePeriod[] {
  const start = parseDate(date), end = new Date(start); end.setUTCDate(end.getUTCDate() + 90);
  const periods: FinancePeriod[] = [];
  const cursor = new Date(start);
  if (granularity === "month") cursor.setUTCDate(1);
  while (cursor <= end) {
    const first = new Date(cursor), last = new Date(cursor);
    if (granularity === "month") last.setUTCMonth(last.getUTCMonth() + 1, 0);
    if (granularity === "week") last.setUTCDate(last.getUTCDate() + 6);
    const from = first < start ? new Date(start) : first, to = last > end ? new Date(end) : last;
    const key = granularity === "month" ? iso(cursor).slice(0, 7) : granularity === "week" ? `${iso(from)}/${iso(to)}` : iso(from);
    const label = granularity === "month"
      ? new Intl.DateTimeFormat("tr-TR", { month: "long", year: "numeric", timeZone: "UTC" }).format(cursor)
      : granularity === "day" ? shortDay(from) : `${shortDay(from)} – ${shortDay(to)}`;
    periods.push({ key, label, from: iso(from), to: iso(to), partial: first < start || last > end });
    if (granularity === "month") cursor.setUTCMonth(cursor.getUTCMonth() + 1, 1);
    else cursor.setUTCDate(cursor.getUTCDate() + (granularity === "week" ? 7 : 1));
  }
  return periods;
}
export const financeTimelineMonths = (date: string) => financeTimelinePeriods(date, "month");

export function financeSourceIssue(book: WorkbookDashboard, id: string): FinanceSourceIssue | null {
  const sheet = book.sheets.find(item => item.id === id);
  if (book.errors.some(error => error.id === id)) return { sheetId: id, kind: "error", message: "Kaynak okunamadı; tutar sıfır kabul edilmedi." };
  if (!sheet) return { sheetId: id, kind: "missing", message: "Kaynak henüz okunmadı." };
  if (sheet.hasMore) {
    const direction = id === "tahsilatlar" ? "receivables" : id === "tedarikci-odemeleri" ? "payables" : null;
    const coverage = book.coverage?.find(item => item.direction === direction);
    const label = id === "tahsilatlar" ? "Müşteri tahsilatları" : id === "tedarikci-odemeleri" ? "Tedarikçi ödemeleri"
      : book.catalog.find(item => item.id === id)?.name ?? id;
    return { sheetId: id, kind: "partial", message: coverage
      ? `${label}: ${coverage.total === undefined ? `${coverage.read} hesap okundu; toplam hesap sayısı henüz doğrulanmadı` : `${coverage.read} / ${coverage.total} hesap okundu`}.${coverage.failed ? ` ${coverage.failed} hesapta okuma hatası var.` : ""}${!coverage.discoveryComplete ? " Tarama sürüyor." : ""}`
      : `${label}: kaynak kapsamı henüz tamamlanmadı.` };
  }
  if (sheet.date && sheet.date !== book.date) return { sheetId: id, kind: "wrong-date", message: "Kaynak tarihi rapor günüyle eşleşmiyor." };
  if (sheet.company && book.company && sheet.company !== book.company) return { sheetId: id, kind: "wrong-company", message: "Kaynak şirketi raporla eşleşmiyor." };
  return null;
}

const fixedSources = [
  { id: "100", label: "Kasalar", kind: "opening" }, { id: "102", label: "Banka hesapları", kind: "opening" },
  { id: "101", label: "Alınan çekler", kind: "incoming" }, { id: "121", label: "Alacak senetleri", kind: "incoming" },
  { id: "103", label: "Verilen çekler", kind: "outgoing" }, { id: "321", label: "Borç senetleri", kind: "outgoing" },
  { id: "kredi-taksitleri", label: "Kredi taksitleri", kind: "outgoing" },
] as const;
const additionalSources = [
  { id: "tahsilatlar", label: "Müşteri tahsilatları", kind: "incoming" },
  { id: "tedarikci-odemeleri", label: "Tedarikçi ödemeleri", kind: "outgoing" },
  { id: "leasing", label: "Leasing · anapara ve faiz", kind: "outgoing" },
] as const;

/** A single source catalog is shared by the timeline, detail inspector and totals. */
export function financeCashSources(book: WorkbookDashboard, includePlans = false): FinanceCashSource[] {
  const definitions: { id: string; sheetId?: string; label: string; kind: FinanceCashSource["kind"]; category?: string }[] = [
    ...fixedSources, ...additionalSources.filter(item => book.catalog?.some(page => page.id === item.id) || book.sheets.some(sheet => sheet.id === item.id)),
  ];
  const plans = book.sheets.find(sheet => sheet.id === "manuel-plan");
  for (const [category, label] of Object.entries(financePlanLabels)) for (const kind of ["incoming", "outgoing"] as const) {
    if (plans?.rows.some(row => row.code === category && row.direction === kind)) definitions.push({
      id: `plan-${category}-${kind}`, sheetId: "manuel-plan", category, kind, label: `${label} · kullanıcı planı`,
    });
  }
  return definitions.map(definition => {
    const sheetId = definition.sheetId ?? definition.id, issue = financeSourceIssue(book, sheetId);
    return { ...definition, sheetId, issue, available: issue === null,
      included: sheetId === "leasing" ? includePlans : true };
  });
}

export function financeSourceRows(book: WorkbookDashboard, source: FinanceCashSource) {
  const sheet = book.sheets.find(item => item.id === source.sheetId);
  if (!sheet) return [];
  return workbookUniqueRows(sheet).filter(row => {
    if (source.category && row.code !== source.category) return false;
    if (source.kind !== "opening" && row.direction !== source.kind) return false;
    return source.sheetId === "leasing" ? !row.status.startsWith("Faturalanmış") : workbookRowIncluded(sheet, row);
  });
}

export function financeCashRecords(book: WorkbookDashboard, includePlans = false): FinanceCashRecord[] {
  return financeCashSources(book, includePlans).flatMap(source => financeSourceRows(book, source).map(row => ({
    key: `${source.sheetId}:${row.id}`, sheetId: source.sheetId, movementId: source.kind === "opening" ? "opening" : source.id,
    row, amount: source.kind === "outgoing" ? -BigInt(row.remaining) : BigInt(row.remaining), included: source.included && source.available,
  })));
}

export function financeCashCurrencies(book: WorkbookDashboard, includePlans = false): string[] {
  const records = financeCashRecords(book, includePlans);
  const all = [...new Set(records.map(record => record.row.currency))];
  const active = all.filter(currency => records.some(record => record.row.currency === currency && record.amount !== 0n));
  const selected = active.length ? active : all.length ? all : ["TL"];
  const priority = ["TL", "USD", "EUR", "GBP"];
  return selected.sort((left, right) => {
    const a = priority.indexOf(left), b = priority.indexOf(right);
    return (a < 0 ? priority.length : a) - (b < 0 ? priority.length : b) || left.localeCompare(right);
  });
}

export function financeOpeningBalance(book: WorkbookDashboard, currency: string, basis: FinanceCashBasis = "recorded"): bigint | null {
  const recorded = workbookLaneTotal(book, "cash", currency);
  if (recorded === null || basis === "recorded") return recorded;
  const declaration = book.cashAvailability?.find(item => item.date === book.date && item.currency === currency);
  return declaration ? recorded - BigInt(declaration.restrictedAmount) : null;
}

export function financeAvailabilityIssues(book: WorkbookDashboard, currencies: readonly string[], basis: FinanceCashBasis = "recorded"): FinanceSourceIssue[] {
  return basis === "recorded" ? [] : currencies.filter(currency => !book.cashAvailability?.some(item => item.date === book.date && item.currency === currency))
    .map(currency => ({ sheetId: `availability:${currency}`, kind: "missing", message: `${currency} için rapor gününe ait kullanılabilir nakit beyanı yok.` }));
}

function availabilityRecords(book: WorkbookDashboard, currencies: readonly string[]): FinanceCashRecord[] {
  return currencies.flatMap(currency => {
    const declaration = book.cashAvailability?.find(item => item.date === book.date && item.currency === currency);
    if (!declaration) return [];
    const id = `${book.date}:${currency}`, amount = declaration.restrictedAmount;
    return [{ key: `availability:${id}`, sheetId: "availability", movementId: "availability", amount: -BigInt(amount), included: true,
      row: { id, code: "KULLANICI-BEYANI", name: "Kullanıcı beyanına göre nakit kısıtı", document: declaration.note,
        date: book.date, currency, amount, remaining: amount, paid: null, direction: "outgoing", status: "Kullanıcı beyanı · Mikro kaydı değildir" } }];
  });
}

const aggregate = new Set(["opening", "incoming-total", "outgoing-total", "net", "closing"]);
function scopeSources(book: WorkbookDashboard, scope: FinanceDetailScope) {
  return financeCashSources(book, scope.includePlans).filter(source => {
    if (scope.category && source.category !== scope.category) return false;
    if (scope.direction && source.kind !== scope.direction) return false;
    if (scope.rowId === "opening" && scope.from <= book.date) return source.kind === "opening";
    if (scope.rowId === "opening" || scope.rowId === "closing") return source.kind === "opening" || source.included;
    if (scope.rowId === "incoming-total") return source.kind === "incoming" && source.included;
    if (scope.rowId === "outgoing-total") return source.kind === "outgoing" && source.included;
    if (scope.rowId === "net") return source.kind !== "opening" && source.included;
    return source.id === scope.rowId || source.sheetId === scope.rowId;
  });
}

function recordInScope(record: FinanceCashRecord, book: WorkbookDashboard, scope: FinanceDetailScope) {
  if (scope.currency && record.row.currency !== scope.currency) return false;
  if (scope.category && record.row.code !== scope.category) return false;
  if (scope.direction && record.row.direction !== scope.direction) return false;
  const day = record.row.date;
  if (scope.bucket === "overdue") return record.movementId !== "opening" && Boolean(day && day < book.date);
  if (scope.bucket === "undated") return record.movementId !== "opening" && day == null;
  if (record.movementId === "opening") return scope.rowId === "opening" || scope.rowId === "closing" || ["100", "102"].includes(scope.rowId);
  if (!day || day < book.date) return false;
  if (scope.rowId === "opening") return day < scope.from;
  if (scope.rowId === "closing") return day <= scope.to;
  return day >= scope.from && day <= scope.to;
}

/** Exact source records behind a cell, including the carried cash calculation for opening/closing cells. */
export function financeWorkbookDetails(book: WorkbookDashboard, scope: FinanceDetailScope): FinanceDetailResult {
  parseDate(scope.from); parseDate(scope.to);
  if (scope.from > scope.to) throw new Error("Başlangıç tarihi bitiş tarihinden sonra olamaz.");
  const sources = scopeSources(book, scope), sourceIds = new Set(sources.map(source => source.id));
  const records = financeCashRecords(book, scope.includePlans).filter(record => {
    const matchesSource = record.movementId === "opening" ? sourceIds.has(record.sheetId) : sourceIds.has(record.movementId);
    return matchesSource && recordInScope(record, book, scope);
  });
  const issues = sources.flatMap(source => source.issue ? [source.issue] : []);
  if (!sources.length && !aggregate.has(scope.rowId)) issues.push({ sheetId: scope.rowId, kind: "missing", message: "Seçili nakit kalemi artık bu raporda bulunmuyor." });
  const undated = sources.filter(source => source.kind !== "opening" && financeSourceRows(book, source)
    .some(row => (!scope.currency || row.currency === scope.currency) && row.date == null && BigInt(row.remaining) !== 0n));
  if ((scope.bucket ?? "period") === "period") for (const source of undated) issues.push({ sheetId: source.sheetId,
    kind: "undated", message: "Vadesi bilinmeyen kayıtlar bu döneme yerleştirilemedi." });
  const blockingUndated = new Set(undated.filter(source => book.sheets.find(sheet => sheet.id === source.sheetId)?.kind !== "open").map(source => source.id));
  const currencies = scope.currency ? [scope.currency] : financeCashCurrencies(book, scope.includePlans);
  const availableCarry = scope.basis === "available" && ["opening", "closing"].includes(scope.rowId) && (scope.bucket ?? "period") === "period";
  if (availableCarry) {
    issues.push(...financeAvailabilityIssues(book, currencies, "available"));
    records.push(...availabilityRecords(book, currencies));
  }
  const totals = currencies.map(currency => {
    const unavailable = sources.some(source => !source.available) || (!sources.length && !aggregate.has(scope.rowId))
      || (availableCarry && financeOpeningBalance(book, currency, "available") === null);
    const unknownDate = (scope.bucket ?? "period") === "period" && sources.some(source => blockingUndated.has(source.id)
      && financeSourceRows(book, source).some(row => row.currency === currency && row.date == null && BigInt(row.remaining) !== 0n));
    return { currency, amount: unavailable || unknownDate ? null : records.filter(record => record.row.currency === currency)
      .reduce((sum, record) => sum + record.amount, 0n) };
  });
  const uniqueIssues = [...new Map(issues.map(issue => [`${issue.sheetId}:${issue.kind}`, issue])).values()];
  return { records, totals, issues: uniqueIssues, complete: uniqueIssues.length === 0 && totals.every(total => total.amount !== null) };
}

/** Converts at the same source/period boundaries as the displayed timeline, including carry-forward rounding. */
export function financeWorkbookDetailTotal(book: WorkbookDashboard, scope: FinanceDetailScope, currency: DisplayCurrency): bigint | null {
  const detail = financeWorkbookDetails(book, scope);
  if (detail.totals.some(total => total.amount === null)) return null;
  if (currency === "original") return detail.totals.length === 1 ? detail.totals[0]?.amount ?? null : null;
  if (detail.totals.some(total => convertWorkbookAmount(0n, total.currency, currency, book.context, book.date) === null)) return null;
  const carry = (scope.rowId === "opening" || scope.rowId === "closing") && (scope.bucket ?? "period") === "period";
  const periods = carry ? financeTimelinePeriods(book.date, scope.granularity ?? "month") : [];
  const buckets = new Map<string, { currency: string; amount: bigint }>();
  const currencies = new Set(detail.totals.map(total => total.currency));
  for (const record of detail.records) {
    // Keep inactive zero-balance accounts inspectable without introducing a currency
    // that the timeline deliberately excludes from its conversion scope.
    if (!currencies.has(record.row.currency)) continue;
    const opening = record.movementId === "opening" || record.movementId === "availability";
    const period = carry && !opening
      ? periods.find(item => record.row.date && record.row.date >= item.from && record.row.date <= item.to)?.key ?? "outside" : "selected";
    const key = `${record.row.currency}:${opening ? "opening" : record.movementId}:${period}`;
    const existing = buckets.get(key);
    buckets.set(key, { currency: record.row.currency, amount: (existing?.amount ?? 0n) + record.amount });
  }
  let total = 0n;
  for (const bucket of buckets.values()) {
    const amount = convertWorkbookAmount(bucket.amount, bucket.currency, currency, book.context, book.date);
    if (amount === null) return null;
    total += amount;
  }
  return total;
}
